import { AxiosResponse } from 'axios';
import { DictionaryLanguageTranslation } from '@constants';

import { ListItemsResponse, OrgUnitItemResponse } from '@innowise-group/core';

import { $api } from '../axios/axios';

interface TaxResidencyResponse {
  id: number;
  name: string;
}

export default class DictionaryService {
  static async getDictionarySelectOptions(): Promise<AxiosResponse> {
    return $api.get(`/recruiting-tool/api/v1/translation/grouped`);
  }

  static async getCountrySelectOptions(body = {}): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/countries/getByFilter`, { ...body });
  }

  static async getCitySelectOptions(body: { hasCompanyOffice?: boolean }, lang?: string): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/cities/search`, body, !!lang && { headers: { 'Accept-Language': lang } });
  }

  static async addCityToDictionary(data: {
    countryId: string;
    cityNames: { [key: string]: string };
  }): Promise<AxiosResponse> {
    return $api.post('/recruiting-tool/api/v1/cities', data);
  }

  static async mergeLocations(data: object): Promise<AxiosResponse> {
    return $api.post('/recruiting-tool/api/v1/cities/merge', data);
  }

  static async createStatus(data: object): Promise<AxiosResponse> {
    return $api.post('/recruiting-tool/api/v1/status', data);
  }

  static async updateStatusName(id: string, data: object): Promise<AxiosResponse> {
    return $api.put(`/recruiting-tool/api/v1/status/${id}`, data);
  }

  static async getStatusById(id: string): Promise<AxiosResponse> {
    return $api.get(`/recruiting-tool/api/v1/status/${id}`);
  }

  static async deleteStatusById(id: string): Promise<AxiosResponse> {
    return $api.delete(`/recruiting-tool/api/v1/status/${id}`);
  }

  static async restoreStatusById(id: string): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/status/restore/${id}`);
  }

  static async addSubStatus(data: object): Promise<AxiosResponse> {
    return $api.post('/recruiting-tool/api/v1/status/addSubStatus', data);
  }

  static async getOrgUnitsByTypeId(id: number): Promise<AxiosResponse<OrgUnitItemResponse[]>> {
    return $api.post('/recruiting-tool/api/v1/org-unit/search', { typeId: id });
  }

  static async getTaxResidencyOptions({
    page,
    name,
  }: {
    page: number;
    name: string;
  }): Promise<AxiosResponse<ListItemsResponse<TaxResidencyResponse>>> {
    return $api.post(
      '/recruiting-tool/api/v1/tax-residency/getAll',
      { name },
      {
        params: { page, size: 20 },
      },
    );
  }

  static async searchDictionaryValues(params: {
    page: number;
    size?: number;
    searchText?: string;
    domainTypes?: Array<string>;
    parentIds?: string[];
    sortDirection?: string;
    sortLanguage?: string;
  }): Promise<AxiosResponse> {
    const { sortDirection, page, size, ...restParams } = params;

    return $api.post('/recruiting-tool/api/v1/admin/dictionary/search', restParams, {
      params: { sort: `translation,${sortDirection.toLowerCase()}`, page, size },
    });
  }

  static async createDictionaryValues(params: {
    languageTranslationMapping: DictionaryLanguageTranslation;
    domainTypename: string;
    parentId?: string;
  }): Promise<AxiosResponse> {
    return $api.post(`/recruiting-tool/api/v1/admin/dictionary`, params, {
      ignoreGlobalCatch: true,
    });
  }

  static async updateDictionaryValues(params: {
    languageTranslationMapping: DictionaryLanguageTranslation;
    domainTypename: string;
    unionTag: string;
    parentId?: string;
  }): Promise<AxiosResponse> {
    const { unionTag, ...restParams } = params;

    return $api.put(`/recruiting-tool/api/v1/admin/dictionary/${unionTag}`, restParams, {
      ignoreGlobalCatch: true,
    });
  }
}
